/**
 * steward workflow store — session + turns + transient streaming state.
 *
 * Persist discipline (HARD RULE): only durable fields are persisted
 * (`sessionId` + `messages`). Streaming state — `status`, `streamingSteps`,
 * `error` — is TRANSIENT and lives in memory only, so a
 * page reload can NEVER rehydrate `status: 'streaming'` and leave a dead
 * in-flight block on screen.
 *
 * User-isolation guard (copied from useConciergeStore): runs synchronously at
 * module load, BEFORE Zustand hydrates, and wipes the persisted store if the
 * Supabase auth-token user_id changed — so user A's filing chat never appears
 * under user B on the same browser.
 *
 * The streaming orchestration (the `streamSteward` call) lives in the page,
 * not here — this store exposes pure state actions the page drives.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import type {
  StewardStageOutput,
  StewardStageType,
  StewardToolEvent,
  StewardSessionSummary,
  StewardSessionDetail,
} from '@/lib/api'

// =============================================================================
// CRITICAL: User isolation — runs SYNCHRONOUSLY before store hydration.
// =============================================================================

const STEWARD_STORE_KEY = 'steward-store-v1'
const STEWARD_USER_ID_KEY = 'steward-store-user-id'

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(STEWARD_USER_ID_KEY)
    const hasStore = localStorage.getItem(STEWARD_STORE_KEY) !== null

    let currentUserId: string | null = null
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const raw = localStorage.getItem(key)
          const authData = JSON.parse(raw || '{}')
          currentUserId =
            authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch {
          /* skip invalid JSON */
        }
      }
    }

    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      localStorage.removeItem(STEWARD_STORE_KEY)
      localStorage.setItem(STEWARD_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && hasStore && currentUserId) {
      localStorage.removeItem(STEWARD_STORE_KEY)
      localStorage.setItem(STEWARD_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && currentUserId) {
      localStorage.setItem(STEWARD_USER_ID_KEY, currentUserId)
    } else if (!currentUserId && hasStore) {
      localStorage.removeItem(STEWARD_STORE_KEY)
    }
  } catch {
    /* localStorage may be unavailable; silent failure is fine */
  }
}
clearStoreIfUserMismatch()

// =============================================================================
// Types
// =============================================================================

export type Role = 'user' | 'assistant'

/** A rich card rendered inline in the chat. Only the *reference* is held (path/name);
 *  the card reloads its content from the API (persist discipline — never store content). */
export type CardRef =
  | { kind: 'job'; path: string }
  | { kind: 'file'; path: string; name: string; ext: string }
  | { kind: 'note'; noteKind: string; id: string; title?: string }

export interface Message {
  id: string
  role: Role
  /** For assistant turns this is the FINAL markdown from session_complete. */
  content: string
  createdAt: number
  /** When present, render as a card (job brief / file preview) instead of text. */
  card?: CardRef
  /** The folded per-node timeline that produced this assistant turn (snapshot of
   *  the live steps at session_complete). Absent on user turns and on sessions
   *  rehydrated from the server (which only stores role+content). */
  steps?: TimelineStep[]
}

/** A tool invocation captured inside a step (e.g. `semantic_search`). */
export interface StepTool {
  tool: string
  label?: string
  count?: number
  status: 'running' | 'done'
}

/** One node's slice of the live turn: it started, maybe thought, maybe called
 *  tools, then finished. Rendered as one row in the folding timeline. */
export interface TimelineStep {
  stage: StewardStageType
  status: 'running' | 'done'
  /** Set from `<node>_done` (carries the node output or a skipped/empty flag). */
  output?: StewardStageOutput
  /** Extended-thinking text accumulated while this node ran (deep mode). */
  thinking: string
  tools: StepTool[]
}

export type StewardStatus = 'idle' | 'streaming' | 'error'

interface StewardState {
  // ── Durable (persisted) ──
  sessionId: string | null
  sessionName: string | null
  messages: Message[]

  // ── Transient (in-memory only — NEVER persisted) ──
  sessions: StewardSessionSummary[]   // the picker list — refetched, never persisted
  /** Specialist (surgeon) mode: true while in a `/commonplace_keeper` session. Transient —
   *  the entry gate re-emits `specialist_active` on every specialist turn, so it self-heals
   *  after a reload; a session switch resets it (the next turn re-derives). */
  specialistActive: boolean
  specialistHarness: string
  status: StewardStatus
  /** The live per-node timeline for the in-flight turn. Ordered; the last entry
   *  is whichever node is currently running. */
  streamingSteps: TimelineStep[]
  error: string | null

  // ── Actions ──
  appendUserTurn: (content: string) => void
  startStreaming: () => void
  /** A node began — push a fresh running step. */
  startStep: (stage: StewardStageType) => void
  /** Append a thinking delta to the currently-running step. */
  appendThinking: (delta: string) => void
  /** A tool started/finished inside the currently-running step. */
  addTool: (tool: StewardToolEvent) => void
  /** A node finished — close out its step with the node output. */
  completeStep: (stage: StewardStageType, output: StewardStageOutput) => void
  /** Set specialist (surgeon) mode from a `specialist_active` event. */
  setSpecialist: (ev: { active: boolean; exited: boolean; harness: string }) => void
  finishStreaming: (final: { response: string; sessionId?: string }) => void
  setError: (message: string) => void
  newSession: () => void
  setSessions: (sessions: StewardSessionSummary[]) => void
  setSessionName: (name: string | null) => void
  loadSession: (detail: StewardSessionDetail) => void
  openJobCard: (path: string, title?: string) => void
  appendFileCard: (file: { path: string; name: string; ext: string }) => void
  appendNoteCard: (ref: { noteKind: string; id: string; title?: string }) => void
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

// Reset shape for all transient streaming fields. Spread wherever we need to
// return to a clean, non-streaming state.
const idleStreaming = {
  status: 'idle' as StewardStatus,
  streamingSteps: [] as TimelineStep[],
}

/** Immutably replace the LAST step in the list (the currently-running node),
 *  via an updater. Returns the same array reference unchanged if empty. */
function mapLastStep(
  steps: TimelineStep[],
  fn: (step: TimelineStep) => TimelineStep
): TimelineStep[] {
  if (steps.length === 0) return steps
  const next = steps.slice()
  next[next.length - 1] = fn(next[next.length - 1])
  return next
}

export const useStewardStore = create<StewardState>()(
  persist(
    (set) => ({
      sessionId: null,
      sessionName: null,
      messages: [],
      sessions: [],
      specialistActive: false,
      specialistHarness: '',
      ...idleStreaming,
      error: null,

      appendUserTurn: (content) =>
        set((s) => ({
          messages: [
            ...s.messages,
            { id: newId(), role: 'user', content, createdAt: Date.now() },
          ],
        })),

      startStreaming: () =>
        set({
          ...idleStreaming,
          status: 'streaming',
          error: null,
        }),

      startStep: (stage) =>
        set((s) => ({
          streamingSteps: [
            ...s.streamingSteps,
            { stage, status: 'running', thinking: '', tools: [] },
          ],
        })),

      appendThinking: (delta) =>
        set((s) => ({
          streamingSteps: mapLastStep(s.streamingSteps, (step) => ({
            ...step,
            thinking: step.thinking + delta,
          })),
        })),

      addTool: (tool) =>
        set((s) => ({
          streamingSteps: mapLastStep(s.streamingSteps, (step) => {
            if (tool.status === 'running') {
              return {
                ...step,
                tools: [
                  ...step.tools,
                  { tool: tool.tool, label: tool.label, status: 'running' },
                ],
              }
            }
            // tool_done — close the last still-running tool of this step.
            const tools = step.tools.slice()
            for (let i = tools.length - 1; i >= 0; i--) {
              if (tools[i].status === 'running') {
                tools[i] = { ...tools[i], status: 'done', count: tool.count }
                break
              }
            }
            return { ...step, tools }
          }),
        })),

      // Close the running step for this stage with its node output. A skipped
      // node emits `<node>_done` with no preceding `_start`, so there's no
      // running step to close — those are harmless no-ops here.
      completeStep: (stage, output) =>
        set((s) => {
          const idx = (() => {
            for (let i = s.streamingSteps.length - 1; i >= 0; i--) {
              const st = s.streamingSteps[i]
              if (st.stage === stage && st.status === 'running') return i
            }
            return -1
          })()
          if (idx === -1) return {}
          const steps = s.streamingSteps.slice()
          steps[idx] = { ...steps[idx], status: 'done', output }
          return { streamingSteps: steps }
        }),

      // Specialist mode toggles on the entry gate's signal. `exited` closes it; otherwise
      // `active` reflects whether this turn is in a surgeon session.
      setSpecialist: ({ active, exited, harness }) =>
        set(() =>
          exited
            ? { specialistActive: false, specialistHarness: '' }
            : { specialistActive: active, specialistHarness: harness }
        ),

      // Pin the final assistant message — snapshotting the folded timeline onto
      // it — then clear all transient streaming state.
      finishStreaming: ({ response, sessionId }) =>
        set((s) => {
          const steps = s.streamingSteps.map((st) => ({
            ...st,
            status: 'done' as const,
            tools: st.tools.map((t) => ({ ...t, status: 'done' as const })),
          }))
          return {
            messages: [
              ...s.messages,
              {
                id: newId(),
                role: 'assistant',
                content: response,
                createdAt: Date.now(),
                ...(steps.length > 0 ? { steps } : {}),
              },
            ],
            sessionId: sessionId || s.sessionId,
            ...idleStreaming,
            error: null,
          }
        }),

      setError: (message) =>
        set({
          ...idleStreaming,
          status: 'error',
          error: message,
        }),

      newSession: () =>
        set({
          sessionId: null,
          sessionName: null,
          messages: [],
          specialistActive: false,
          specialistHarness: '',
          ...idleStreaming,
          error: null,
        }),

      setSessions: (sessions) => set({ sessions }),

      setSessionName: (name) => set({ sessionName: name }),

      // Rehydrate a past session from the server: swap in its id, name, and turns.
      loadSession: (detail) =>
        set({
          sessionId: detail.session_id,
          sessionName: detail.name,
          messages: detail.turns.map((t, i) => ({
            id: newId(),
            role: t.role,
            content: t.content,
            createdAt: Date.now() + i,
          })),
          specialistActive: false,
          specialistHarness: '',
          ...idleStreaming,
          error: null,
        }),

      // Open a Job as a card in a FRESH session (the calendar deep-link entry).
      openJobCard: (path, title) =>
        set({
          sessionId: null,
          sessionName: title || 'Job',
          messages: [
            { id: newId(), role: 'assistant', content: '', createdAt: Date.now(), card: { kind: 'job', path } },
          ],
          specialistActive: false,
          specialistHarness: '',
          ...idleStreaming,
          error: null,
        }),

      // Append a file as a card to the current chat (clicking a working link).
      appendFileCard: (file) =>
        set((s) => ({
          messages: [
            ...s.messages,
            { id: newId(), role: 'assistant', content: '', createdAt: Date.now(),
              card: { kind: 'file', path: file.path, name: file.name, ext: file.ext } },
          ],
        })),

      // Open a clicked reference (job / area / category / project) as a card in the chat.
      appendNoteCard: (ref) =>
        set((s) => ({
          messages: [
            ...s.messages,
            { id: newId(), role: 'assistant', content: '', createdAt: Date.now(),
              card: { kind: 'note', noteKind: ref.noteKind, id: ref.id, title: ref.title } },
          ],
        })),
    }),
    {
      name: STEWARD_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist ONLY durable fields. Transient streaming state must never
      // rehydrate — a reload must NOT come back with status === 'streaming'.
      partialize: (state) => ({
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        messages: state.messages,
      }),
    }
  )
)
